import { useSpring } from '@react-spring/web';
import Image from 'next/image';
import { CSSProperties } from 'react';

import { AnimatedDiv } from '@/utils/animated-div';

import styles from './animations.module.css';

interface AnimatedCardProps {
  src: string;
  width: number;
  height: number;
  alt: string;
  onLoad?: () => void;
  style?: CSSProperties;
  isLoading?: boolean;
  /** Bypass the image optimizer, e.g. for `blob:` object URLs. */
  unoptimized?: boolean;
}

export const AnimatedCard = ({
  src,
  width,
  height,
  alt,
  onLoad,
  style,
  isLoading,
  unoptimized,
}: AnimatedCardProps) => {
  const [props, set] = useSpring(() => ({
    xys: [0, 0, 1],
    config: { mass: 1, tension: 350, friction: 40 },
  }));

  const calc = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return [-(y - centerY) / 20, (x - centerX) / 20, 1.03];
  };

  const trans = (x: number, y: number, s: number): string =>
    `perspective(1500px) rotateX(${x}deg) rotateY(${y}deg) scale(${s})`;

  return (
    <div className="relative w-full max-w-[434px]" style={{ perspective: '1500px' }}>
      <AnimatedDiv
        className={`relative w-full rounded-[16px] will-change-transform motion-reduce:transform-none! shadow-[0_10px_30px_-5px_rgba(0,0,0,0.3)] transition-shadow duration-500 hover:shadow-[0_30px_100px_-10px_rgba(0,0,0,0.4)] ${styles['shine-overlay']}`}
        onMouseMove={(e) => {
          const values = calc(e);
          set({ xys: values });
        }}
        onMouseLeave={() => set({ xys: [0, 0, 1] })}
        style={{
          transform: props.xys.to(trans),
        }}
      >
        <Image
          src={src}
          width={width}
          height={height}
          alt={alt}
          unoptimized={unoptimized}
          onLoad={onLoad}
          className="relative z-0 rounded-[16px] w-full block"
          style={{
            height: isLoading ? '0' : height,
            ...style,
          }}
        />
      </AnimatedDiv>
    </div>
  );
};
